<script setup lang="ts">
/**
 * Die obere Leiste: wo bin ich, und was gehört zum PROJEKT.
 *
 * WARUM SIE JETZT ETWAS ZU TUN BEKOMMT. Sie trug Logo, drei Reiter und das
 * Konto — sonst nichts. Die Leiste darunter trug dafür drei Ebenen auf einmal:
 * die Anwendung (Zurück-Pfeil), das Projekt (Name, Bereich, Verlauf, Zugriff,
 * Vorschau-Link, Kataloge, Auf Live) und die offene Seite. David am
 * 19.08.2026: „was ist mit der leiste oben? da nutzen wir noch fast kein platz
 * aus."
 *
 * WIE ES DIE GROSSEN MACHEN. Contentful teilt seine Navbar in benannte Zonen:
 * Hauptnavigation, ein „switcher"-Feld für den Arbeitsbereich, und rechts das
 * Sekundäre (Suche, Hilfe, Konto). Sanity hat oben denselben Schnitt:
 * Arbeitsbereich, globale Suche, Konto — und darunter erst das Dokument. Bei
 * uns heißt Arbeitsbereich schlicht: Projekt.
 *
 * DIE REGEL DARAUS: oben das Projekt, unten die Seite. Merksatz für die zwei
 * Hauptknöpfe: unten sichern, oben veröffentlichen.
 *
 * Was hier hineinkommt, kommt über die beiden Slots — `projekt` links für die
 * Brotkrume, `aktionen` rechts. Die Projektliste lässt beide leer und sieht
 * aus wie vorher.
 *
 * DIE LEISTE MISST SICH SELBST und gibt das Ergebnis an ihre Slots weiter
 * (`zeigt(stufe)`). Nur sie kennt ihre Breite; was bei zu wenig Platz als
 * Erstes weichen soll, weiß dagegen nur der Inhalt. Deshalb misst sie und der
 * Inhalt entscheidet — dieselbe Aufteilung wie in der Leiste darunter.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useVonAussen } from '@/composables/useVonAussen'
import { useLeistenPlatz } from '@/composables/useLeistenPlatz'
import { useBefehle } from '@/composables/useBefehle'
import ThemeToggle from '@/components/ThemeToggle.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { user, logout } = useAuth()

/**
 * Ein Kundenzugang sieht keine Reiter.
 *
 * Nicht aus Ordnungsliebe: Die Wache im Router wirft ihn bei jedem dieser
 * Klicks in seine eigene Ansicht zurück (`router/index.ts`, Kunden-Käfig).
 * Drei Links, die nichts tun, sind schlechter als keine — und dieselbe Regel
 * gilt in der Leiste darunter schon seit dem 15.08.2026.
 */
const vonAussen = useVonAussen()
const befehle = useBefehle()

const isProjectsActive = computed(() => route.path.startsWith('/projects'))
const isSettingsActive = computed(() => route.path.startsWith('/settings'))
const isAuditActive = computed(() => route.path.startsWith('/audit'))

async function onLogout() {
  await logout()
  await router.push('/login')
}

/* ===== Wie viel Platz ist da? ==========================================
 *
 * Fünf Stufen, weil die Handgriffe des Projekts fünf sind (seit der Mediathek,
 * 27.10.2026). Welcher Knopf an welcher Stufe hängt, steht in
 * `ProjektAktionen` — hier wird nur gemessen.
 */
const leiste = ref<HTMLElement | null>(null)
const STUFEN = 5
const platz = useLeistenPlatz(STUFEN, () => {
  const el = leiste.value
  return el !== null && el.scrollWidth > el.clientWidth + 1
})

let beobachter: ResizeObserver | null = null
onMounted(() => {
  void platz.pruefe()
  if (typeof ResizeObserver !== 'undefined' && leiste.value !== null) {
    beobachter = new ResizeObserver(() => void platz.pruefe())
    beobachter.observe(leiste.value)
  }
})
onBeforeUnmount(() => beobachter?.disconnect())

// Der Projektname ist verschieden lang, und die Reiter kommen und gehen mit
// der Rolle. Beides ändert, was noch hineinpasst.
watch(() => [route.path, vonAussen.value], () => void platz.pruefe())
</script>

<template>
  <header
    ref="leiste"
    class="sticky top-0 z-30 h-10 flex items-center gap-2 px-3 text-xs
           border-b border-line bg-surface/85 backdrop-blur shrink-0
           overflow-x-auto"
  >
    <!-- Marke. Für einen Kundenzugang kein Link: Die Projektliste ist für ihn
         gesperrt, ein Klick landete in seiner eigenen Ansicht zurück. -->
    <RouterLink
      v-if="!vonAussen"
      to="/projects"
      class="flex items-center gap-2 group shrink-0"
    >
      <span
        class="inline-grid place-items-center w-7 h-7 rounded-md font-bold
               tracking-tight text-[12px] bg-primary-dark text-white
               shadow-sm shadow-primary/20"
        aria-hidden="true"
      >m</span>
      <span class="hidden sm:inline text-sm font-semibold text-fg
                   group-hover:text-primary transition-colors">{{ t('app.name') }}</span>
    </RouterLink>
    <span
      v-else
      class="inline-grid place-items-center w-7 h-7 rounded-md font-bold
             tracking-tight text-[12px] bg-primary-dark text-white shrink-0"
      aria-hidden="true"
    >m</span>

    <!--
      DAS PROJEKT DIREKT NEBEN DER MARKE (David, 19.08.2026: „mach das projekt
      neben miracms 2.0"). Genau so macht es Sanity: Der Arbeitsbereich hängt
      als Wähler unmittelbar am Studio-Titel, und erst danach kommen die
      Werkzeuge. Es liest sich als eine Zeile: wo bin ich, in welchem Projekt.

      Hier stand bis heute stattdessen der Pfad der Adresse in
      Schreibmaschinenschrift („/projects/underwave/edit"). Das ist etwas für
      uns beim Suchen eines Fehlers, nicht für Nahm.
    -->
    <slot name="projekt" :zeigt="platz.zeigt" />

    <span
      v-if="!vonAussen && $slots.projekt"
      class="w-px h-4 bg-line shrink-0 mx-1"
      aria-hidden="true"
    />

    <nav v-if="!vonAussen" class="flex items-center gap-0.5" :aria-label="t('ui.hauptnavigation')">
      <RouterLink
        to="/projects"
        :class="[
          'px-2 py-1 rounded font-medium transition-colors',
          isProjectsActive ? 'text-fg bg-hover' : 'text-fg-mid hover:text-fg hover:bg-hover',
        ]"
      >{{ t('nav.projects') }}</RouterLink>
      <RouterLink
        to="/audit"
        :class="[
          'px-2 py-1 rounded font-medium transition-colors',
          isAuditActive ? 'text-fg bg-hover' : 'text-fg-mid hover:text-fg hover:bg-hover',
        ]"
      >{{ t('nav.audit') }}</RouterLink>
      <RouterLink
        to="/settings"
        :class="[
          'px-2 py-1 rounded font-medium transition-colors',
          isSettingsActive ? 'text-fg bg-hover' : 'text-fg-mid hover:text-fg hover:bg-hover',
        ]"
      >{{ t('nav.settings') }}</RouterLink>
    </nav>

    <div class="ml-auto flex items-center gap-1.5">
      <!-- Was man mit dem PROJEKT tut. -->
      <slot name="aktionen" :zeigt="platz.zeigt" />

      <span
        v-if="$slots.aktionen"
        class="w-px h-4 bg-line shrink-0 mx-1"
        aria-hidden="true"
      />

      <!--
        AB HIER GEHT ES NICHT MEHR UM DAS PROJEKT, sondern um die Anwendung:
        suchen, hell oder dunkel, wer ich bin. Deshalb stehen sie zusammen und
        hinter dem Trenner — die Suche stand bis eben MITTEN in den
        Projekt-Handgriffen, und das mischt zwei Ebenen in einer Reihe.

        Contentful nennt diese Zone „Secondary navigation (Search, Help,
        Settings, Account)" und setzt sie ganz nach rechts. Der sichtbare Knopf
        ist dabei wichtiger als das Kürzel: Ein Tastenkürzel, das nirgends
        steht, kennt nur, wer es gebaut hat.
      -->
      <button
        type="button"
        class="inline-flex items-center gap-1.5 px-2 py-1 rounded shrink-0
               text-fg-mid hover:text-fg hover:bg-hover transition-colors"
        :title="t('kopf.suchen_titel')"
        :aria-label="t('kopf.suchen')"
        @click="befehle.oeffnen()"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <span class="hidden lg:inline">{{ t('kopf.suchen') }}</span>
        <kbd class="hidden lg:inline font-mono text-[10px] text-fg-muted
                    border border-line rounded px-1 py-0.5">⌘K</kbd>
      </button>

      <ThemeToggle />
      <div v-if="user"
           class="flex items-center gap-1.5 pl-2 ml-1 border-l border-line">
        <span
          class="hidden sm:inline font-mono text-fg-muted text-[10px]"
          :title="`${user.name} (${user.role})`"
        >{{ user.username }}</span>
        <button
          type="button"
          class="inline-grid place-items-center w-6 h-6 rounded
                 text-fg-mid hover:text-primary hover:bg-hover transition-colors"
          :title="t('nav.logout')"
          :aria-label="t('nav.logout')"
          @click="onLogout"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round"
               aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      </div>
    </div>
  </header>
</template>
